/* eslint-disable no-plusplus,no-unused-vars,max-len */
/* global G */
import { curry, setKey } from 'lib/util'
import { findGroup } from 'lib/util/attachment'

/**
 * Clean up the residual event listeners and transient properties.
 *
 * @param {Gaia.AppModule.Spec} module              the current module composition object
 * @param {Gaia.Component.Spec} component           the current action's main component
 * @param {Gaia.Adapter.Attachment[]} [attachments] optional attachments for the message
 * @param {Function} handler                        event handler for each attachment
 * @private
 */
const _cleanup = (module, component, attachments, handler) => {
  const eventAdapter = module[G.ADAPTER][G.EVENTS]
  for (let i = 0; i < attachments.length; i++) {
    eventAdapter.remove(eventAdapter.type(G.ATTACHMENT, G.READ, attachments[i].key), handler)

    // Removing the local attachments we just successfully uploaded. So the user see's an empty
    // dropzone again and can upload something else.
    eventAdapter.dispatch(
      eventAdapter.type(G.ATTACHMENT, G.REMOVE, 'message'),
      { [G.DATA]: attachments[i] },
    )
  }
}

/**
 * Attempts to activate a draft message (and all of its attachments) by emitting a
 * `G.MESSAGE, G.ACTIVATE` event either after all attachments have successfully
 * been uploaded, or, in case there are no attachments, immediately.
 *
 * @param {Gaia.AppModule.Spec} module              the current module composition object
 * @param {Gaia.Component.Spec} component           the current action's main component
 * @param {Gaia.Model.Message} draftMessage         the message to activate
 * @param {Gaia.} [attachments] optional attachments for the note
 * @param {() => void} [callback]                   callback to execute after the message is active
 * @returns {Promise<boolean>}
 */
const activateMessageAndAttachments = async (module, component, draftMessage, attachments, callback) => {
  const eventBus = module[G.ADAPTER][G.EVENTS]

  return new Promise((resolve) => {
    if (attachments && attachments.length) {
      for (let i = 0; i < attachments.length; i++) {
        const handler = ({ detail }) => {
          /**
           * The `G.ATTACHMENT, G.READ, key` event handler is the correct one to listen to.
           * It will get fired when the server returns a response after uploading an attachment.
           * However, it also gets fired before that. So in order to catch the correct event
           * we check for `status === 201`.
           */
          if (detail.key === attachments[i].key && detail.status === 201) {
            const key = detail?.response?.[0]?.id
            setKey(key, 'id', attachments[i])
          }

          if (!attachments.find(att => !att.id)) {
            _cleanup(module, component, attachments, handler)
            eventBus.dispatch(eventBus.type(G.MESSAGE, G.ACTIVATE), {
              [G.DATA]: draftMessage,
              [G.FN]: callback,
            })
            resolve(true)
          }
        }
        eventBus.add(eventBus.type(G.ATTACHMENT, G.READ, attachments[i].key), handler)
      }
    } else {
      eventBus.dispatch(eventBus.type(G.MESSAGE, G.ACTIVATE), {
        [G.DATA]: draftMessage,
        [G.FN]: callback,
      })
      resolve(true)
    }
  })
}

export default curry(activateMessageAndAttachments)
